// utils/sawtXlsExport.js
//
// SAWT monthly summary as a spreadsheet, alongside (never replacing) the DAT
// export. Same source records, same period scoping, different output format.
//
// ── Why hand-built SpreadsheetML rather than a library ───────────────────
// The codebase has no spreadsheet dependency — only papaparse, which is CSV and
// cannot carry number formats. SpreadsheetML 2003 is plain XML that Excel,
// LibreOffice and Google Sheets all open as a real .xls workbook, and it gives
// what the format actually needs: typed numeric cells (`ss:Type="Number"`) with
// a `#,##0.00` display format, rather than pre-formatted strings with embedded
// commas that a reader cannot sum.
//
// ── The two entity types in this document ────────────────────────────────
// They are different parties and must not be conflated:
//   • The BIR FORM 1701/1702 header is the FILER's type   -> utils/sawtFormType
//   • The CORPORATION / INDIVIDUAL column is each PAYEE's -> utils/payeeEntity

import { cleanTIN } from './tinFormat';
import { resolvePayeeEntity } from './payeeEntity';

const MONTHS = [
  'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
  'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER',
];

const COL_COUNT = 9;
const DASH = '----------';
const DOUBLE = '==========';

const xmlEscape = (v) =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const textCell = (value, styleId) =>
  `<Cell${styleId ? ` ss:StyleID="${styleId}"` : ''}><Data ss:Type="String">${xmlEscape(value)}</Data></Cell>`;

// A real number cell. The value is written unformatted; the STYLE carries the
// thousands separator, so the cell stays summable in the spreadsheet.
const numberCell = (value, styleId) =>
  `<Cell ss:StyleID="${styleId}"><Data ss:Type="Number">${Number(value) || 0}</Data></Cell>`;

const emptyCell = () => '<Cell/>';

const row = (cells) => `<Row>${cells.join('')}</Row>`;
const blankRow = () => '<Row/>';

/** A full-width rule row, e.g. the dashed separator under the column headers. */
const ruleRow = (glyph) => row(Array.from({ length: COL_COUNT }, () => textCell(glyph, 'sRule')));

/**
 * "DELA CRUZ, JUAN SANTOS" — the INDIVIDUAL column header states the order as
 * Last, First, Middle. Names are stored as one string, so the split is
 * positional: first token is the surname, last token the middle name.
 */
const formatIndividualName = (fullName) => {
  const parts = String(fullName || '').trim().replace(/,/g, '').split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].toUpperCase();
  const last = parts[0];
  const middle = parts.length > 2 ? parts[parts.length - 1] : '';
  const first = parts.slice(1, parts.length > 2 ? -1 : undefined).join(' ');
  return [last, first, middle].filter(Boolean).join(', ').toUpperCase();
};

const STYLES = `
 <Styles>
  <Style ss:ID="Default" ss:Name="Normal"><Alignment ss:Vertical="Bottom"/>
   <Font ss:FontName="Calibri" ss:Size="11"/></Style>
  <Style ss:ID="sTitle"><Font ss:FontName="Calibri" ss:Size="11" ss:Bold="1"/></Style>
  <Style ss:ID="sLabel"><Font ss:FontName="Calibri" ss:Size="11" ss:Bold="1"/></Style>
  <Style ss:ID="sHead">
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1"/>
   <Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:WrapText="1"/></Style>
  <Style ss:ID="sNumHead">
   <Font ss:FontName="Calibri" ss:Size="10"/>
   <Alignment ss:Horizontal="Center"/></Style>
  <Style ss:ID="sRule"><Font ss:FontName="Calibri" ss:Size="10"/></Style>
  <Style ss:ID="sText"><Font ss:FontName="Calibri" ss:Size="10"/></Style>
  <Style ss:ID="sSeq"><Font ss:FontName="Calibri" ss:Size="10"/>
   <Alignment ss:Horizontal="Center"/></Style>
  <Style ss:ID="sTin"><Font ss:FontName="Calibri" ss:Size="10"/>
   <Alignment ss:Horizontal="Left"/></Style>
  <Style ss:ID="sMoney"><Font ss:FontName="Calibri" ss:Size="10"/>
   <NumberFormat ss:Format="#,##0.00"/></Style>
  <Style ss:ID="sRate"><Font ss:FontName="Calibri" ss:Size="10"/>
   <Alignment ss:Horizontal="Center"/><NumberFormat ss:Format="0.00"/></Style>
  <Style ss:ID="sTotalLabel"><Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1"/>
   <Alignment ss:Horizontal="Right"/></Style>
  <Style ss:ID="sTotalMoney"><Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1"/>
   <NumberFormat ss:Format="#,##0.00"/></Style>
 </Styles>`;

/**
 * Build the SAWT monthly summary workbook.
 *
 * @param {object} args
 * @param {object} args.client            filer (AccountingClient)
 * @param {string} args.formCode          '1701' | '1702' — from isCorporateFiler(client)
 * @param {number} args.month             1-12
 * @param {number} args.year
 * @param {Array}  args.rows              [{ tin, name, payeeEntity, atcCode, natureOfPayment,
 *                                           baseAmount, withholdingRate, taxWithheld }]
 * @returns {string} SpreadsheetML document
 */
export function buildSawtXls({ client, formCode, month, year, rows: dataRows }) {
  const branch = String(client?.branchCode || '0000');
  const tin = cleanTIN(client?.tin);
  const monthName = MONTHS[(Number(month) || 1) - 1] || '';

  const out = [];

  // ── Header block ───────────────────────────────────────────────────────
  out.push(row([textCell(`BIR FORM ${formCode}`, 'sTitle')]));
  out.push(row([textCell('SUMMARY ALPHALIST OF WITHHOLDING TAXES (SAWT)', 'sTitle')]));
  out.push(row([textCell(`FOR THE MONTH OF ${monthName}, ${year}`, 'sTitle')]));
  out.push(blankRow());
  out.push(row([textCell(`TIN : ${tin || ''}-${branch}`, 'sLabel')]));
  // The label reads PAYEE'S NAME but carries the FILER's own registered name.
  // That is what the accepted format does — do not "correct" it to PAYOR.
  out.push(row([textCell(`PAYEE'S NAME: ${String(client?.name || '').toUpperCase()}`, 'sLabel')]));
  out.push(blankRow());

  // ── Column headers ─────────────────────────────────────────────────────
  out.push(row([
    textCell('SEQ NO', 'sHead'),
    textCell('TAXPAYER IDENTIFICATION NUMBER', 'sHead'),
    textCell('CORPORATION (Registered Name)', 'sHead'),
    textCell('INDIVIDUAL (Last Name, First Name, Middle Name)', 'sHead'),
    textCell('ATC CODE', 'sHead'),
    textCell('NATURE OF PAYMENT', 'sHead'),
    textCell('AMOUNT OF INCOME PAYMENT', 'sHead'),
    textCell('TAX RATE', 'sHead'),
    textCell('AMOUNT OF TAX WITHHELD', 'sHead'),
  ]));

  // (1)-(8): SEQ NO is an unnumbered row counter, the eight data columns are numbered.
  out.push(row([
    emptyCell(),
    ...Array.from({ length: 8 }, (_, i) => textCell(`(${i + 1})`, 'sNumHead')),
  ]));

  out.push(ruleRow(DASH));

  // ── Data rows ──────────────────────────────────────────────────────────
  let grandTotalWithheld = 0;

  dataRows.forEach((r, i) => {
    const isCorp = r.payeeEntity === 'corporation';
    const name = String(r.name || '').toUpperCase();
    grandTotalWithheld += Number(r.taxWithheld) || 0;

    out.push(row([
      textCell(String(i + 1), 'sSeq'),
      textCell(r.tin || '', 'sTin'),
      // The payee's OWN entity type decides which column their name lands in.
      isCorp ? textCell(name, 'sText') : emptyCell(),
      isCorp ? emptyCell() : textCell(formatIndividualName(r.name), 'sText'),
      textCell(r.atcCode || '', 'sText'),
      textCell(r.natureOfPayment || '', 'sText'),
      numberCell(r.baseAmount, 'sMoney'),
      // Plain value: 1% is 1.00, never 0.01 and never "1%".
      numberCell(r.withholdingRate, 'sRate'),
      numberCell(r.taxWithheld, 'sMoney'),
    ]));
  });

  // ── Grand total ────────────────────────────────────────────────────────
  out.push(ruleRow(DASH));
  out.push(row([
    emptyCell(), emptyCell(), emptyCell(), emptyCell(), emptyCell(), emptyCell(), emptyCell(),
    textCell('GRAND TOTAL', 'sTotalLabel'),
    numberCell(grandTotalWithheld, 'sTotalMoney'),
  ]));
  out.push(ruleRow(DOUBLE));

  const columns = [
    60, 150, 220, 220, 70, 200, 130, 60, 130,
  ].map((w) => `<Column ss:Width="${w}"/>`).join('');

  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">${STYLES}
 <Worksheet ss:Name="SAWT">
  <Table>${columns}
${out.join('\n')}
  </Table>
 </Worksheet>
</Workbook>`;
}

/**
 * Map a Form2307Record to the shape buildSawtXls expects.
 *
 * NATURE OF PAYMENT is the ATC's own description (AtcCode.description) — not
 * the record's free-text description, which is a bookkeeping memo and does not
 * describe the nature of the payment in BIR's terms. Callers pass it in from
 * the ATC reference table; an unresolved code leaves the cell empty rather than
 * substituting the memo, and the caller is expected to block first.
 */
export function toSawtXlsRow(record, { atcDescription } = {}) {
  const vendor = record.vendor || {};
  return {
    tin: cleanTIN(vendor.tin || record.payerTin || ''),
    name: vendor.name || record.payerName || record.vendorName || '',
    payeeEntity: resolvePayeeEntity(vendor.businessType || record.payerType),
    atcCode: record.atcCode || '',
    natureOfPayment: atcDescription || '',
    baseAmount: Number(record.baseAmount) || 0,
    withholdingRate: Number(record.withholdingRate) || 0,
    taxWithheld: Number(record.taxWithheld) || 0,
  };
}

/** Filename mirrors the DAT convention: {TIN}{branch}{MM}{YYYY}SAWT.xls */
export function sawtXlsFilename({ client, month, year }) {
  const tin = cleanTIN(client?.tin) || '000000000';
  const branch = String(client?.branchCode || '0000');
  return `${tin}${branch}${String(month).padStart(2, '0')}${year}SAWT.xls`;
}

export function downloadSawtXls(xml, filename) {
  const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
